import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

/**
 * Automation & Self-Healing Summary.
 *
 * Surfaces how much manual IT effort the automated monitoring and remediation engines
 * are saving — rule coverage, alert activity, remediation outcomes, and an estimate of
 * manual hours saved. Consumes the read-only aggregation endpoint exposed by
 * SystemHealthController (GET /api/SystemHealth/automation-summary).
 *
 * Every figure is derived at runtime from live alert/execution history; nothing is
 * hardcoded. The deterministic narrative renders regardless of whether AI insights are
 * enabled, so the report stays self-explanatory for directors and an incoming IT lead.
 */
const AutomationSelfHealingSummary = () => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await apiFetch('/api/SystemHealth/automation-summary?days=30');
        if (!response.ok) throw new Error('Failed to fetch automation summary');
        const json = await response.json();
        if (!ignore) setData(json);
      } catch (e) {
        Sentry.captureException(e);
        if (!ignore) { setError(e.message); setData(null); }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [apiFetch]);

  // Composite health score for the automation programme. Blends remediation success
  // (does self-healing actually work?), self-healing coverage (how much resolves itself
  // without a ticket?) and rule automation coverage (how much of the estate is wired for
  // auto-remediation?). Each sub-signal degrades gracefully when there is no activity.
  const healthScore = useMemo(() => {
    if (!data) return null;

    const alertsRaised = data.alertsRaised ?? 0;
    const alertsAutoResolved = data.alertsAutoResolved ?? 0;
    const realExecutions = (data.remediationsExecuted ?? 0) - (data.remediationsSimulated ?? 0);
    const activeRules = (data.activeSystemRules ?? 0) + (data.activeEndpointRules ?? 0);

    // Success of real remediations (neutral 70 when nothing ran yet).
    const successSignal = realExecutions > 0 ? (data.remediationSuccessRate ?? 0) : 70;
    // Share of alerts that healed themselves (neutral 70 when no alerts were raised).
    const selfHealSignal = alertsRaised > 0 ? (alertsAutoResolved / alertsRaised) * 100 : 70;
    // Automation coverage of the active rule estate (neutral 60 when no rules exist).
    const coverageSignal = activeRules > 0
      ? Math.min(100, ((data.autoRemediationEnabledRules ?? 0) / activeRules) * 100)
      : 60;

    const score = Math.round(successSignal * 0.5 + selfHealSignal * 0.3 + coverageSignal * 0.2);

    let interpretation, color, icon;
    if (score >= 90) { interpretation = 'Excellent'; color = '#2e7d32'; icon = '✓'; }
    else if (score >= 80) { interpretation = 'Good'; color = '#7cb342'; icon = '✓'; }
    else if (score >= 70) { interpretation = 'Fair'; color = '#ffa000'; icon = '⚠'; }
    else if (score >= 60) { interpretation = 'Needs Attention'; color = '#ff5722'; icon = '⚠'; }
    else { interpretation = 'Critical'; color = '#d32f2f'; icon = '✗'; }

    return { score, interpretation, color, icon };
  }, [data]);

  // Remediation outcomes (bar chart)
  const remediationOutcomes = useMemo(() => {
    if (!data) return [];
    return [
      { name: 'Completed', value: data.remediationsCompleted || 0, color: '#2e7d32' },
      { name: 'Simulated', value: data.remediationsSimulated || 0, color: '#1976d2' },
      { name: 'Failed', value: data.remediationsFailed || 0, color: '#d32f2f' }
    ].filter(item => item.value > 0);
  }, [data]);

  // Remediation trigger split (pie chart) — how much ran automatically vs by hand
  const triggerSplit = useMemo(() => {
    if (!data) return [];
    return [
      { name: 'Automatic', value: data.remediationsAutomatic || 0, color: '#2e7d32' },
      { name: 'Manual', value: data.remediationsManual || 0, color: '#ed6c02' }
    ].filter(item => item.value > 0);
  }, [data]);

  // Deterministic "what this means / what to improve" narrative.
  const narrative = useMemo(() => {
    if (!data) return null;

    const findings = [];
    const actions = [];

    const activeRules = (data.activeSystemRules ?? 0) + (data.activeEndpointRules ?? 0);
    const autoRules = data.autoRemediationEnabledRules ?? 0;
    const alertsRaised = data.alertsRaised ?? 0;
    const alertsAutoResolved = data.alertsAutoResolved ?? 0;
    const realExecutions = (data.remediationsExecuted ?? 0) - (data.remediationsSimulated ?? 0);
    const failed = data.remediationsFailed ?? 0;
    const totalWizards = data.totalActiveWizards ?? 0;
    const linkedWizards = data.wizardsLinkedToActiveRules ?? 0;
    const unlinkedWizards = Math.max(0, totalWizards - linkedWizards);

    // Coverage
    if (activeRules === 0) {
      findings.push('No monitoring rules are currently active, so the platform is not yet watching any services or devices automatically.');
      actions.push('Activate the System Monitoring and Health Rules that matter most to your environment from the Settings page.');
    } else if (autoRules === 0) {
      findings.push(`${activeRules} monitoring rule(s) are active, but none are wired for auto-remediation — every alert still needs a person to act.`);
      actions.push('Attach a remediation wizard to your highest-frequency rules so common issues can self-heal.');
    } else {
      const coverage = Math.round((autoRules / activeRules) * 100);
      if (coverage < 50) {
        findings.push(`Only ${coverage}% of active rules (${autoRules}/${activeRules}) have auto-remediation enabled.`);
        actions.push('Extend auto-remediation to more rules to reduce manual workload further.');
      }
    }

    // Wizard linkage diagnostic — helps distinguish "quiet period" from "orphaned wizards".
    // If wizards exist but are not attached to rules they can never fire automatically.
    if (totalWizards > 0 && unlinkedWizards > 0) {
      findings.push(
        `${totalWizards} remediation wizard(s) are active, but ${unlinkedWizards} ` +
        `${unlinkedWizards === 1 ? 'is' : 'are'} not linked to any active monitoring rule and ` +
        `will never trigger automatically — they can only be run manually by IT staff.`
      );
      actions.push(
        `Go to Settings → Endpoint Health Rules or System Health Rules and attach the unlinked ` +
        `wizard(s) to a relevant rule, then enable auto-remediation on that rule.`
      );
    } else if (totalWizards > 0 && linkedWizards === totalWizards && alertsRaised === 0) {
      // All wizards are linked but nothing fired — reassure the user the setup is correct.
      findings.push(
        `All ${totalWizards} active wizard(s) are linked to monitoring rules. No conditions ` +
        `breached their thresholds this period — no automatic runs were needed.`
      );
    }

    // Self-healing effectiveness
    if (alertsRaised > 0) {
      const selfHealPct = Math.round((alertsAutoResolved / alertsRaised) * 100);
      if (selfHealPct >= 50) {
        findings.push(`${selfHealPct}% of alerts (${alertsAutoResolved}/${alertsRaised}) recovered on their own without manual intervention.`);
      } else if (alertsAutoResolved > 0) {
        findings.push(`${selfHealPct}% of alerts auto-recovered; the remainder required a technician.`);
        actions.push('Review the most frequent non-self-healing alerts and build remediation wizards for them.');
      }
    }

    // Remediation reliability
    if (failed > 0) {
      findings.push(`${failed} remediation run(s) failed and fell back to manual handling.`);
      actions.push('Investigate the failed remediation wizards and harden their steps (timeouts, retries, approval gates).');
    }

    // Value delivered
    if ((data.estimatedManualHoursSaved ?? 0) > 0) {
      findings.push(`Automation is estimated to have saved roughly ${data.estimatedManualHoursSaved} hour(s) of manual IT effort this period.`);
    }

    let context;
    const score = healthScore?.score ?? 0;
    if (realExecutions === 0 && alertsRaised === 0) {
      context = 'The automation engine is configured but has had little to react to this period — a quiet month is a good sign. As alerts occur, this section will show how much work the platform is absorbing on your behalf.';
    } else if (score >= 80) {
      context = 'Self-healing automation is working well: a healthy share of issues are detected and resolved without manual effort.';
    } else if (score >= 60) {
      context = 'Automation is delivering value but has room to grow — extending coverage and tuning wizards will reduce the IT team\u2019s manual load further.';
    } else {
      context = 'Automation is still being established. Expanding rule coverage and attaching remediation wizards will progressively reduce manual workload.';
    }

    return { context, findings, actions };
  }, [data, healthScore]);

  // LLM proxy text for AI summary + OverallHealthScore consumption
  const llmProxyText = useMemo(() => {
    if (!data) return '';
    const totalWizards = data.totalActiveWizards ?? 0;
    const linkedWizards = data.wizardsLinkedToActiveRules ?? 0;
    return [
      `Automation & Self-Healing Health Score: ${healthScore?.score}/100 (${healthScore?.interpretation})`,
      `Window: last ${data.windowDays} days`,
      `Active Rules: ${(data.activeSystemRules ?? 0) + (data.activeEndpointRules ?? 0)} (${data.autoRemediationEnabledRules ?? 0} auto-remediation enabled)`,
      `Active Wizards: ${totalWizards} total, ${linkedWizards} linked to active rules, ${Math.max(0, totalWizards - linkedWizards)} unlinked (manual-only)`,
      `Alerts Raised: ${data.alertsRaised ?? 0}, Currently Active: ${data.alertsCurrentlyActive ?? 0}, Auto-Resolved: ${data.alertsAutoResolved ?? 0}`,
      `Remediations Executed: ${data.remediationsExecuted ?? 0} (Completed: ${data.remediationsCompleted ?? 0}, Failed: ${data.remediationsFailed ?? 0}, Simulated: ${data.remediationsSimulated ?? 0})`,
      `Trigger Split: ${data.remediationsAutomatic ?? 0} automatic, ${data.remediationsManual ?? 0} manual`,
      `Remediation Success Rate: ${data.remediationSuccessRate ?? 0}% (excludes simulated)`,
      `Issues Resolved by Remediation: ${data.issuesResolvedByRemediation ?? 0}`,
      `Estimated Manual Effort Saved: ${data.estimatedManualHoursSaved ?? 0} hours (${data.estimatedManualMinutesSaved ?? 0} minutes)`
    ].join('\n');
  }, [data, healthScore]);

  if (loading) return <HexagonLoader message="Loading automation & self-healing data..." />;
  if (error) return <div className="dashboard-card error-card"><p>Error: {error}</p></div>;
  if (!data) return <div className="dashboard-card info-card"><p>No automation data available</p></div>;

  const totalRules = (data.activeSystemRules ?? 0) + (data.activeEndpointRules ?? 0);
  const selfHealRate = (data.alertsRaised ?? 0) > 0
    ? Math.round(((data.alertsAutoResolved ?? 0) / data.alertsRaised) * 100)
    : 0;

  // Wizard linkage breakdown for display
  const totalWizardsDisplay = data.totalActiveWizards ?? 0;
  const linkedWizardsDisplay = data.wizardsLinkedToActiveRules ?? 0;
  const unlinkedWizardsDisplay = Math.max(0, totalWizardsDisplay - linkedWizardsDisplay);
  // Highlight unlinked wizards in amber so IT staff can act on the gap
  const wizardLinkageColor = unlinkedWizardsDisplay > 0 ? '#ff9800' : '#2e7d32';

  return (
    <div className={`dashboard-card group-wrapper automation-selfhealing-summary${isPrintMode ? ' print-mode' : ''}`}>
      <div data-llm-proxy style={{ display: 'none' }}>{llmProxyText}</div>

      <h2 style={{ marginTop: 0, marginBottom: 12 }}>Automation &amp; Self-Healing</h2>

      {/* Health Score Banner — matches the standard exec-banner-layout used by every other
          top-level report section (Sentry, GitHub, Backup, Device Compliance, etc.) so
          this section doesn't stand out as a visual outlier. */}
      {healthScore && (
        <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${healthScore.color}` }}>
          <div className="exec-banner-layout">
            <div className="exec-banner-score">
              <div className="exec-banner-value" style={{ color: healthScore.color }}>
                {healthScore.score}
                <span className="exec-banner-suffix">/ 100</span>
              </div>
              <div className="exec-banner-label" style={{ color: healthScore.color }}>{healthScore.icon} {healthScore.interpretation}</div>
              <div className="exec-banner-sublabel">Automation Health (last {data.windowDays} days)</div>
            </div>
            <div className="exec-banner-body">
              <div className="exec-banner-text">
                <strong>What this means:</strong> {narrative?.context}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deterministic explanation block — always shown so directors and the incoming
          IT lead understand the score even when AI insights are disabled. */}
      {narrative && (narrative.findings.length > 0 || narrative.actions.length > 0) && (
        <div
          className="dashboard-card"
          style={{
            borderLeft: `4px solid ${healthScore?.color || '#777'}`,
            background: '#fafbfc',
            padding: '12px 16px',
            marginBottom: 12
          }}
        >
          {narrative.findings.length > 0 && (
            <div style={{ marginBottom: narrative.actions.length ? 8 : 0 }}>
              <div style={{ fontSize: 12, textTransform: 'uppercase', color: '#666', marginBottom: 4 }}>Highlights</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                {narrative.findings.map((f, i) => <li key={`f-${i}`}>{f}</li>)}
              </ul>
            </div>
          )}
          {narrative.actions.length > 0 && (
            <div>
              <div style={{ fontSize: 12, textTransform: 'uppercase', color: '#666', marginBottom: 4 }}>Recommended next steps</div>
              <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                {narrative.actions.map((a, i) => <li key={`a-${i}`}>{a}</li>)}
              </ol>
            </div>
          )}
        </div>
      )}

      {/* Key Metrics */}
      <div className="metrics-row">
        <div className="dashboard-card metric-card">
          <div className="metric-label">Monitoring Coverage</div>
          <div className="metric-value">{totalRules}</div>
          <div className="metric-subtitle">
            {data.autoRemediationEnabledRules ?? 0} wired for auto-remediation
          </div>
        </div>

        {/* Wizard coverage — shows active wizards and how many are linked to a rule.
            Helps IT staff distinguish "nothing triggered" (good) from "wizards aren't
            attached to any rule" (configuration gap). */}
        <div className="dashboard-card metric-card">
          <div className="metric-label">Active Wizards</div>
          <div className="metric-value" style={{ color: wizardLinkageColor }}>
            {totalWizardsDisplay}
          </div>
          <div className="metric-subtitle" style={{ color: unlinkedWizardsDisplay > 0 ? '#ff9800' : undefined }}>
            {linkedWizardsDisplay} linked • {unlinkedWizardsDisplay} unlinked
          </div>
        </div>

        <div className="dashboard-card metric-card">
          <div className="metric-label">Alerts Raised</div>
          <div className="metric-value">{data.alertsRaised ?? 0}</div>
          <div className="metric-subtitle">
            {data.alertsCurrentlyActive ?? 0} active • {data.alertsAutoResolved ?? 0} self-healed
          </div>
        </div>

        <div className="dashboard-card metric-card">
          <div className="metric-label">Remediation Success</div>
          <div className="metric-value">{data.remediationSuccessRate ?? 0}%</div>
          <div className="metric-subtitle">
            {data.remediationsCompleted ?? 0} completed • {data.remediationsFailed ?? 0} failed
          </div>
        </div>

        <div className="dashboard-card metric-card">
          <div className="metric-label">Manual Effort Saved</div>
          <div className="metric-value">{data.estimatedManualHoursSaved ?? 0}h</div>
          <div className="metric-subtitle">
            {selfHealRate}% of alerts auto-recovered
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-row">
        {/* Remediation Outcomes */}
        <div className="dashboard-card chart-card">
          <h3>Remediation Outcomes</h3>
          {remediationOutcomes.length > 0 ? (
            <ResponsiveContainer width="100%" height={isPrintMode ? 200 : 250}>
              <BarChart data={remediationOutcomes}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#2196F3" isAnimationActive={!isPrintMode}>
                  {remediationOutcomes.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="chart-empty" style={{ color: '#888', fontSize: 13 }}>No remediation runs in this period.</p>
          )}
        </div>

        {/* Trigger Split */}
        <div className="dashboard-card chart-card">
          <h3>Remediation Trigger Split</h3>
          {triggerSplit.length > 0 ? (
            <ResponsiveContainer width="100%" height={isPrintMode ? 200 : 250}>
              <PieChart>
                <Pie
                  data={triggerSplit}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={isPrintMode ? 70 : 80}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  isAnimationActive={!isPrintMode}
                >
                  {triggerSplit.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="chart-empty" style={{ color: '#888', fontSize: 13 }}>No remediation runs to attribute yet.</p>
          )}
        </div>
      </div>

      {/* Detail Section */}
      <div className="metrics-row">
        <div className="dashboard-card info-section">
          <h3>Self-Healing Activity</h3>
          <div className="status-grid">
            <div className="status-item">
              <span className="status-label">Alerts Auto-Resolved:</span>
              <span className="status-value">{data.alertsAutoResolved ?? 0}</span>
              <span className="status-detail">({selfHealRate}% of {data.alertsRaised ?? 0})</span>
            </div>
            <div className="status-item">
              <span className="status-label">Issues Fixed by Remediation:</span>
              <span className="status-value">{data.issuesResolvedByRemediation ?? 0}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Currently Active Alerts:</span>
              <span className={`status-value ${(data.alertsCurrentlyActive ?? 0) > 0 ? 'warn-text' : ''}`}>
                {data.alertsCurrentlyActive ?? 0}
              </span>
            </div>
            <div className="status-item">
              <span className="status-label">Estimated Effort Saved:</span>
              <span className="status-value">{data.estimatedManualHoursSaved ?? 0} hrs</span>
              <span className="status-detail">({data.estimatedManualMinutesSaved ?? 0} min)</span>
            </div>
          </div>
        </div>

        <div className="dashboard-card info-section">
          <h3>Remediation Reliability</h3>
          <div className="status-grid">
            <div className="status-item">
              <span className="status-label">Total Runs:</span>
              <span className="status-value">{data.remediationsExecuted ?? 0}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Completed:</span>
              <span className="status-value">{data.remediationsCompleted ?? 0}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Failed:</span>
              <span className={`status-value ${(data.remediationsFailed ?? 0) > 0 ? 'warn-text' : ''}`}>
                {data.remediationsFailed ?? 0}
              </span>
            </div>
            <div className="status-item">
              <span className="status-label">Simulated (non-prod):</span>
              <span className="status-value">{data.remediationsSimulated ?? 0}</span>
            </div>
          </div>
        </div>

        {/* Wizard Coverage — surfaces the gap between available wizards and those actually
            wired to a rule. Without this panel, an IT admin seeing "No runs" has no way to
            tell whether their wizards are correctly set up or silently orphaned. */}
        <div className="dashboard-card info-section">
          <h3>Wizard Coverage</h3>
          <div className="status-grid">
            <div className="status-item">
              <span className="status-label">Active Wizards:</span>
              <span className="status-value">{totalWizardsDisplay}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Linked to a Rule:</span>
              <span className="status-value" style={{ color: '#2e7d32' }}>{linkedWizardsDisplay}</span>
              <span className="status-detail">(can trigger automatically)</span>
            </div>
            <div className="status-item">
              <span className="status-label">Not Linked:</span>
              <span
                className="status-value"
                style={{ color: unlinkedWizardsDisplay > 0 ? '#ff9800' : undefined }}
              >
                {unlinkedWizardsDisplay}
              </span>
              <span className="status-detail">(manual execution only)</span>
            </div>
          </div>
        </div>
      </div>

      <div className="explanation-box">
        <strong>How to read this:</strong>
        <ul>
          <li><strong>Monitoring Coverage</strong> — active rules watching your services/devices; the more wired for auto-remediation, the less manual triage IT performs.</li>
          <li><strong>Active Wizards / Wizard Coverage</strong> — "linked" means the wizard is attached to an active rule with auto-remediation enabled and can fire automatically. "Unlinked" wizards are only run manually by IT staff — attaching them to a rule closes the gap.</li>
          <li><strong>Self-healed alerts</strong> — issues that recovered on their own (no ticket action required).</li>
          <li><strong>Remediation Success</strong> — share of real remediation runs that completed successfully (simulated non-production runs are excluded).</li>
          <li><strong>Manual Effort Saved</strong> — a conservative estimate (≈20 min per successful auto-remediation, ≈10 min per self-healed alert).</li>
        </ul>
      </div>
    </div>
  );
};

export default AutomationSelfHealingSummary;
